import { useState } from "react";
import { TagIcon } from "@phosphor-icons/react/Tag";
import { ControlBarItem } from "@freightsimple/ui-core/PageLayout/ControlBarItem";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { EmptyState } from "@freightsimple/ui-core/Display/EmptyState";
import { Page } from "@freightsimple/ui-core/PageLayout/Page";
import { SearchInput } from "@freightsimple/ui-core/Controls/SearchInput";
import { T } from "@freightsimple/ui-core/Typography/T";
import { OpportunityAgreementStatus } from "@freightsimple/generated-carriers-openapi-client/models/OpportunityAgreementStatus";
import { carriersApi } from "@/App/Auth/carrierApi";
import { OpportunitiesTable } from "@/App/Tables/OpportunitiesTable";
import { CarrierLoadFailed } from "@/App/CarrierLoadFailed";
import { useCarrierFetch } from "@/App/useCarrierFetch";
import { CarrierPageLogo } from "@/App/CarrierPageLogo";
import {
  OPPORTUNITY_FILTERS,
  matchesOpportunityFilter,
  matchesOpportunitySearch,
} from "@/App/Views/opportunitiesFiltering";

/**
 * Customers looking for customer-specific pricing — the business available to
 * this carrier, whether or not an agreement exists yet.
 *
 * This used to list only the carrier's own agreements. It is now a list of
 * companies left-joined to their agreement, because the agreements were the
 * least interesting part: what a carrier wants to know is what freight is on
 * offer and whether they can cover it.
 */
// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function CustomerSpecificPricingView() {
  const { state, refreshing, refresh } = useCarrierFetch(() =>
    carriersApi().getCarrierOpportunities(),
  );
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const opportunities =
    state.status === "loaded" ? state.data.opportunities : undefined;

  const visible = opportunities?.filter(
    (opportunity) =>
      matchesOpportunitySearch(opportunity, search) &&
      matchesOpportunityFilter(opportunity, filter),
  );

  const filtering = search.trim() !== "" || filter !== "all";
  const openCount = opportunities?.filter(
    (opportunity) =>
      opportunity.agreementStatus === OpportunityAgreementStatus.None,
  ).length;

  return (
    <Page
      leadingVisual={<CarrierPageLogo />}
      icon={TagIcon}
      title="Customer Specific Pricing"
      description="Customers looking for pricing, and the accounts you already hold."
      fullBleed
      headerStats={
        opportunities
          ? [
              { label: "Open", value: openCount ?? 0 },
              { label: "Total", value: opportunities.length },
            ]
          : undefined
      }
      refreshing={refreshing}
      filterBar={
        opportunities && opportunities.length > 0 ? (
          <>
            <ControlBarItem label="Search">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Customer, city…"
                width="lg"
              />
            </ControlBarItem>
            <ControlBarItem label="Show">
              <Dropdown
                options={OPPORTUNITY_FILTERS}
                value={filter}
                onChange={setFilter}
                width="md"
              />
            </ControlBarItem>
          </>
        ) : undefined
      }
    >
      {state.status === "loading" && (
        <T size="sm" color="secondary">
          Loading…
        </T>
      )}
      {state.status === "error" && (
        <CarrierLoadFailed what="pricing opportunities" onRetry={refresh} />
      )}
      {visible && visible.length === 0 && filtering && (
        <EmptyState
          icon={TagIcon}
          title="Nothing matches"
          description="Try a different customer, or clear the filter."
        />
      )}
      {visible && (visible.length > 0 || !filtering) && (
        <OpportunitiesTable opportunities={visible} onChanged={refresh} />
      )}
    </Page>
  );
}
